import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Filter, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { proposalsApi, type ListProposalsParams } from '@/api/proposals';
import { unwrapError } from '@/api/client';
import { PROPOSAL_STATUSES, type Proposal, type ProposalStatus } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { JsonView } from '@/components/data/JsonView';
import { DataField } from '@/components/data/DataField';
import { LoadMore } from '@/components/data/LoadMore';
import { formatDate } from '@/lib/utils';

const statusVariant: Record<ProposalStatus, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
  pending: 'default',
  confirmed: 'secondary',
  rejected: 'warning',
  executed: 'success',
  failed: 'destructive',
};

interface Filters {
  userId: string;
  status: ProposalStatus | '';
  toolName: string;
}

const EMPTY: Filters = { userId: '', status: '', toolName: '' };

export function Proposals(): JSX.Element {
  const { t } = useTranslation();
  const [filters, setFilters] = React.useState<Filters>(EMPTY);
  const [applied, setApplied] = React.useState<Filters>(EMPTY);
  const [openId, setOpenId] = React.useState<string | null>(null);

  const queryParams = React.useMemo<ListProposalsParams>(() => {
    const p: ListProposalsParams = { limit: 50 };
    if (applied.userId) p.userId = applied.userId;
    if (applied.status) p.status = applied.status;
    if (applied.toolName) p.toolName = applied.toolName;
    return p;
  }, [applied]);

  const query = useInfiniteQuery({
    queryKey: ['proposals', queryParams],
    queryFn: ({ pageParam }) =>
      proposalsApi.list({ ...queryParams, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });

  const items: Proposal[] = query.data?.pages.flatMap((p) => p.items) ?? [];
  const dirty = JSON.stringify(filters) !== JSON.stringify(applied);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('proposals.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('proposals.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          className="gap-2"
        >
          <RefreshCw className={query.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          {t('common.refresh')}
        </Button>
      </header>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Filter className="h-4 w-4" />
            {t('audit.filtersTitle')}
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t('proposals.userId')}</Label>
              <Input
                value={filters.userId}
                onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('proposals.status')}</Label>
              <Select
                value={filters.status || '__any'}
                onValueChange={(v) =>
                  setFilters({ ...filters, status: v === '__any' ? '' : (v as ProposalStatus) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">{t('common.any')}</SelectItem>
                  {PROPOSAL_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('proposals.toolName')}</Label>
              <Input
                value={filters.toolName}
                onChange={(e) => setFilters({ ...filters, toolName: e.target.value })}
                placeholder={t('proposals.toolNamePlaceholder')}
                className="h-9 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilters(EMPTY);
                setApplied(EMPTY);
              }}
              disabled={!dirty && !applied.userId && !applied.status && !applied.toolName}
            >
              {t('common.reset')}
            </Button>
            <Button size="sm" onClick={() => setApplied(filters)} disabled={!dirty}>
              {t('common.apply')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('common.loading')}
            </div>
          ) : query.error ? (
            <p className="p-6 text-sm text-destructive">{unwrapError(query.error)}</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">{t('proposals.empty')}</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('proposals.tableHeader.created')}</TableHead>
                    <TableHead>{t('proposals.tableHeader.user')}</TableHead>
                    <TableHead>{t('proposals.tableHeader.status')}</TableHead>
                    <TableHead>{t('proposals.tableHeader.tool')}</TableHead>
                    <TableHead>{t('proposals.tableHeader.summary')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => (
                    <TableRow
                      key={row.proposalId}
                      className="cursor-pointer"
                      onClick={() => setOpenId(row.proposalId)}
                    >
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatDate(row.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-xs">{row.userId}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {t('audit.bot')}: {row.botId.slice(0, 12)}…
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant[row.status]}>{row.status}</Badge>
                        {row.cancelledByAdmin ? (
                          <Badge variant="outline" className="ml-1 text-[10px]">
                            {t('proposals.ops')}
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <code className="font-mono text-xs">{row.toolName}</code>
                      </TableCell>
                      <TableCell className="max-w-md">
                        <span className="line-clamp-2 text-xs">{row.summary}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LoadMore
                hasNext={query.hasNextPage ?? false}
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              />
            </>
          )}
        </CardContent>
      </Card>

      <ProposalDetailDialog
        proposalId={openId}
        onClose={() => setOpenId(null)}
        onCancelled={() => void query.refetch()}
      />
    </div>
  );
}

function ProposalDetailDialog({
  proposalId,
  onClose,
  onCancelled,
}: {
  proposalId: string | null;
  onClose: () => void;
  onCancelled: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['proposals', 'detail', proposalId],
    queryFn: () => proposalsApi.get(proposalId!),
    enabled: proposalId !== null,
  });
  const [reason, setReason] = React.useState('');
  const [showCancel, setShowCancel] = React.useState(false);

  React.useEffect(() => {
    if (proposalId === null) {
      setReason('');
      setShowCancel(false);
    }
  }, [proposalId]);

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      proposalsApi.cancel(id, reason),
    onSuccess: (res) => {
      if (res.status === 'rejected' && res.cancelledByAdmin) {
        toast.success(t('proposals.cancelledToast'));
      } else {
        toast.info(t('proposals.alreadyFinalized', { status: res.status }));
      }
      void queryClient.invalidateQueries({ queryKey: ['proposals'] });
      setShowCancel(false);
      onCancelled();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const r = detail.data;
  const canCancel = r?.status === 'pending';

  return (
    <Dialog open={proposalId !== null} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('proposals.detailTitle')}</DialogTitle>
          <DialogDescription>
            <code className="font-mono text-xs">{proposalId}</code>
          </DialogDescription>
        </DialogHeader>
        {detail.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('common.loading')}
          </div>
        ) : detail.error ? (
          <p className="text-sm text-destructive">{unwrapError(detail.error)}</p>
        ) : !r ? null : (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <DataField label={t('proposals.fields.status')} value={<Badge variant={statusVariant[r.status]}>{r.status}</Badge>} />
              <DataField label={t('proposals.fields.tool')} value={r.toolName} mono />
              <DataField label={t('proposals.fields.created')} value={formatDate(r.createdAt)} />
              <DataField label={t('proposals.fields.userId')} value={r.userId} mono />
              <DataField label={t('proposals.fields.bot')} value={r.botId} mono />
              <DataField label={t('proposals.fields.channel')} value={r.channel ?? '—'} mono />
              <DataField label={t('proposals.fields.session')} value={r.sessionId} mono />
              {r.chatSessionId ? (
                <DataField label={t('proposals.fields.chatSession')} value={r.chatSessionId} mono full />
              ) : null}
              {r.confirmedAt ? (
                <DataField label={t('proposals.fields.confirmedAt')} value={formatDate(r.confirmedAt)} />
              ) : null}
              {r.executedAt ? (
                <DataField label={t('proposals.fields.executedAt')} value={formatDate(r.executedAt)} />
              ) : null}
              {r.cancelledByAdmin ? (
                <>
                  <DataField label={t('proposals.fields.cancelledBy')} value={r.cancelledBy} mono />
                  <DataField label={t('proposals.fields.reason')} value={r.cancelReason} full />
                </>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t('proposals.fields.summary')}
              </div>
              <p className="rounded-md border bg-muted/30 p-3 text-sm">{r.summary}</p>
            </div>

            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t('proposals.fields.payload')}
              </div>
              <JsonView value={r.payload} />
            </div>

            {r.result !== undefined ? (
              <div className="space-y-1.5">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t('proposals.fields.executionResult')}
                </div>
                <JsonView value={r.result} />
              </div>
            ) : null}

            {r.error ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-xs">{r.error}</AlertDescription>
              </Alert>
            ) : null}

            {showCancel && canCancel ? (
              <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
                <Label htmlFor="reason" className="text-xs">
                  {t('proposals.cancelReasonLabel')}
                </Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder={t('proposals.cancelReasonPlaceholder')}
                />
              </div>
            ) : null}
          </div>
        )}
        <DialogFooter className="sm:justify-between">
          {canCancel ? (
            showCancel ? (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowCancel(false)}>
                  {t('common.back')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={cancelMutation.isPending}
                  onClick={() =>
                    proposalId && cancelMutation.mutate({ id: proposalId, reason: reason || undefined })
                  }
                  className="gap-1.5"
                >
                  {cancelMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Ban className="h-3 w-3" />
                  )}
                  {t('proposals.confirmCancel')}
                </Button>
              </div>
            ) : (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowCancel(true)}
                className="gap-1.5"
              >
                <Ban className="h-3 w-3" />
                {t('proposals.forceCancel')}
              </Button>
            )
          ) : (
            <div />
          )}
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
